// data-fit LLC · DecisionFlow · ACTIVITY Section
// CalculationsViewer.jsx
// ui/src/components/activity/CalculationsViewer.jsx
//
// S847 — OI-S845-OBSERVE-SECTION implementation.
// RBAC: comp_admin, tenant_super_admin (enforced at router layer + RLS).
// Read-only. No mutations. No approval actions.
//
// Field visibility policy (LOCKED S847):
//   display_phase   ✓ shown as business label
//   phase_name      ✗ NEVER shown (internal engine key)
//   detail JSONB    ✗ NEVER shown (internal engine diagnostic)
//   duration_ms     ✓ formatted to human string by formatDuration()
//
// Pattern: matches AuditLogViewer.jsx + ImportLogViewer.jsx exactly.
//   - useAuth() direct — ARCH-2 compliant
//   - cursor-based pagination (Load More)
//   - client-side search + sort
//   - right side panel on row click (same fixed w-96 z-40 pattern)
//   - telemetry sub-table loaded on panel open

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, X, Activity } from 'lucide-react'
import { useResizablePanel } from '../../hooks/useResizablePanel.js' // S946 — resizable panel

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getErrorEnvelope = (err) => {
  if (err?.error) {
    const { code, message } = err.error
    return { code, message }
  }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.' }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error
      ? payload
      : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload))       return payload
  return []
}

const getPaginationFromEnvelope = (payload) => ({
  next_cursor: payload?.pagination?.next_cursor || null,
  has_more:    Boolean(payload?.pagination?.has_more),
})

const truncateId = (value) => {
  if (!value) return '—'
  const s = String(value)
  return s.length > 12 ? `${s.slice(0, 8)}…` : s
}

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '—'
  const n = Number(ms)
  if (isNaN(n)) return '—'
  if (n < 1000) return `${n}ms`
  if (n < 60000) return `${(n / 1000).toFixed(1)}s`
  const mins = Math.floor(n / 60000)
  const secs = Math.floor((n % 60000) / 1000)
  return `${mins}m ${secs}s`
}

const formatTimestamp = (ts) => {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch {
    return ts
  }
}

// ---------------------------------------------------------------------------
// Status badge — mirrors ImportLogViewer renderStatusBadge
// ---------------------------------------------------------------------------

const STATUS_COLORS = {
  completed:               'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
  failed:                  'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]',
  running:                 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
  pending:                 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  cancelled:               'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  completed_with_exclusions: 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${cls}`}>
      {status || '—'}
    </span>
  )
}

function RunTypeBadge({ runType }) {
  const cls = runType === 'final'
    ? 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]'
    : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${cls}`}>
      {runType || '—'}
    </span>
  )
}

// Phase trace status badge — smaller, used in telemetry sub-table
function PhaseBadge({ status }) {
  const cls = status === 'ok' || status === 'exit_ok'
    ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]'
    : status === 'error' || status === 'failed'
    ? 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]'
    : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
  return status
    ? <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-tok-xs font-medium ${cls}`}>{status}</span>
    : <span className="text-[rgb(var(--color-text-muted))]">—</span>
}

// ---------------------------------------------------------------------------
// Sort icon — matches AuditLogViewer
// ---------------------------------------------------------------------------

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
  return sortDir === 'asc'
    ? <ChevronUp   className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
    : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
}

// ---------------------------------------------------------------------------
// INITIAL_FILTERS
// ---------------------------------------------------------------------------

const INITIAL_FILTERS = {
  fiscal_period_id: '',
  run_type:         '',
  run_status:       '',
}

// OI-CALC-RUN-UI-DURATION-REFRESH: poll while any non-terminal run is visible
const POLL_INTERVAL_MS   = 5000
const NON_TERMINAL_STATUSES = new Set(['pending', 'queued', 'running'])

// ---------------------------------------------------------------------------
// CalculationsViewer
// ---------------------------------------------------------------------------

export default function CalculationsViewer() {
  const { token } = useAuth()

  // Run list state
  const [runs,       setRuns]       = useState([])
  const [filters,    setFilters]    = useState(INITIAL_FILTERS)
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore,    setHasMore]    = useState(false)
  const [isLoading,      setIsLoading]      = useState(false)
  const [isLoadingMore,  setIsLoadingMore]  = useState(false)
  const [errorMessage,   setErrorMessage]   = useState(null)

  // Client-side search + sort
  const [search,  setSearch]  = useState('')
  const [sortCol, setSortCol] = useState('initiated_at')
  const [sortDir, setSortDir] = useState('desc')

  // Side panel + telemetry state
  const [selectedRun,      setSelectedRun]      = useState(null)
  const [telemetry,        setTelemetry]        = useState([])
  const [isLoadingTelem,   setIsLoadingTelem]   = useState(false)
  const [telemError,       setTelemError]       = useState(null)

  // S946 — resizable panel
  const { panelWidth, dragHandleProps } = useResizablePanel({
    storageKey:   'df_panel_calculations',
    defaultWidth: 480,
    minWidth:     320,
    maxWidth:     900,
  })

  // ---------------------------------------------------------------------------
  // Build query params
  // ---------------------------------------------------------------------------

  const buildQueryParams = ({ cursor = null, activeFilters = filters } = {}) => {
    const q = new URLSearchParams()
    if (activeFilters.fiscal_period_id !== '' && !isNaN(Number(activeFilters.fiscal_period_id))) {
      q.set('fiscal_period_id', String(Number(activeFilters.fiscal_period_id)))
    }
    if (activeFilters.run_type   !== '') q.set('run_type',   activeFilters.run_type)
    if (activeFilters.run_status !== '') q.set('run_status', activeFilters.run_status)
    if (cursor) q.set('cursor', cursor)
    return q
  }

  // ---------------------------------------------------------------------------
  // Load runs
  // ---------------------------------------------------------------------------

  const loadRuns = useCallback(
    async ({ cursor = null, append = false, activeFilters = filters } = {}) => {
      if (append) setIsLoadingMore(true)
      else        setIsLoading(true)
      setErrorMessage(null)

      try {
        const q   = buildQueryParams({ cursor, activeFilters })
        const qs  = q.toString()
        const url = qs
          ? `/api/v1/calculation-run-history?${qs}`
          : '/api/v1/calculation-run-history'

        const response = await fetch(url, { method: 'GET', credentials: 'include' })
        const payload  = await parseApiResponse(response)
        const rows     = getRowsFromEnvelope(payload)
        const pag      = getPaginationFromEnvelope(payload)

        setRuns((curr) => append ? [...curr, ...rows] : rows)
        setNextCursor(pag.next_cursor)
        setHasMore(pag.has_more)
      } catch (err) {
        const { code, message } = getErrorEnvelope(err)
        setErrorMessage(message || `Unable to load calculation runs. (${code})`)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [token]
  )

  useEffect(() => { loadRuns({ activeFilters: INITIAL_FILTERS }) }, [loadRuns])

  // OI-CALC-RUN-UI-DURATION-REFRESH: refresh list while any run is non-terminal
  // so duration populates without a manual reload. Clears automatically once
  // all visible runs reach a terminal status.
  useEffect(() => {
    const hasNonTerminal = runs.some((r) => NON_TERMINAL_STATUSES.has(r.run_status))
    if (!hasNonTerminal) return
    const id = setInterval(() => {
      loadRuns({ activeFilters: filters })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [runs, filters, loadRuns])

  // ---------------------------------------------------------------------------
  // Load telemetry for selected run
  // ---------------------------------------------------------------------------

  const loadTelemetry = useCallback(async (runId) => {
    if (!runId) return
    setIsLoadingTelem(true)
    setTelemError(null)
    setTelemetry([])
    try {
      const response = await fetch(
        `/api/v1/calculation-run-history/${runId}/telemetry`,
        { method: 'GET', credentials: 'include' }
      )
      const payload = await parseApiResponse(response)
      setTelemetry(getRowsFromEnvelope(payload))
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setTelemError(message || `Unable to load phase trace. (${code})`)
    } finally {
      setIsLoadingTelem(false)
    }
  }, [token])

  // ---------------------------------------------------------------------------
  // Filter handlers
  // ---------------------------------------------------------------------------

  const handleApplyFilters = () => {
    setRuns([])
    setNextCursor(null)
    loadRuns({ activeFilters: filters })
  }

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS)
    setRuns([])
    setNextCursor(null)
    loadRuns({ activeFilters: INITIAL_FILTERS })
  }

  // ---------------------------------------------------------------------------
  // Row select
  // ---------------------------------------------------------------------------

  const handleSelectRun = (row) => {
    if (selectedRun?.calculation_run_id === row.calculation_run_id) {
      setSelectedRun(null)
      return
    }
    setSelectedRun(row)
    loadTelemetry(row.calculation_run_id)
  }

  const handleClosePanel = () => {
    setSelectedRun(null)
    setTelemetry([])
    setTelemError(null)
  }

  // ---------------------------------------------------------------------------
  // Sort
  // ---------------------------------------------------------------------------

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortCol(col); setSortDir('asc') }
  }

  // ---------------------------------------------------------------------------
  // Client-side filter + sort
  // ---------------------------------------------------------------------------

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return runs.filter((r) =>
      !q ||
      (r.run_type   ?? '').toLowerCase().includes(q) ||
      (r.run_status ?? '').toLowerCase().includes(q) ||
      String(r.fiscal_period_id ?? '').includes(q) ||
      (r.initiated_by ?? '').toLowerCase().includes(q)
    )
  }, [runs, search])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let av = '', bv = ''
      if      (sortCol === 'initiated_at')       { av = a.initiated_at ?? '';       bv = b.initiated_at ?? '' }
      else if (sortCol === 'fiscal_period_id') { av = a.fiscal_period_id ?? 0;  bv = b.fiscal_period_id ?? 0 }
      else if (sortCol === 'run_type')         { av = a.run_type ?? '';          bv = b.run_type ?? '' }
      else if (sortCol === 'run_status')       { av = a.run_status ?? '';        bv = b.run_status ?? '' }
      else if (sortCol === 'duration_ms')      { av = a.duration_ms ?? -1;       bv = b.duration_ms ?? -1 }
      else if (sortCol === 'phase_count')      { av = a.phase_count ?? 0;        bv = b.phase_count ?? 0 }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortCol, sortDir])

  // ---------------------------------------------------------------------------
  // Shared CSS
  // ---------------------------------------------------------------------------

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))] whitespace-nowrap'

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-4">

      {/* Header */}
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Calculations</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">
          Review calculation run history and engine phase trace. This screen is read-only.
        </p>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Filters</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Fiscal Period ID
            <input
              type="number" step="1"
              value={filters.fiscal_period_id}
              onChange={(e) => setFilters((f) => ({ ...f, fiscal_period_id: e.target.value }))}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]"
            />
          </label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Run Type
            <select
              value={filters.run_type}
              onChange={(e) => setFilters((f) => ({ ...f, run_type: e.target.value }))}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]"
            >
              <option value="">All</option>
              <option value="preview">Preview</option>
              <option value="final">Final</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Status
            <select
              value={filters.run_status}
              onChange={(e) => setFilters((f) => ({ ...f, run_status: e.target.value }))}
              className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm text-[rgb(var(--color-text))]"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="completed_with_exclusions">Completed with Exclusions</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleApplyFilters}
            disabled={isLoading}
            className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50"
          >
            Apply Filters
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            disabled={isLoading}
            className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
          {errorMessage}
        </div>
      )}

      {/* Run list table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Calculation Runs
            {runs.length > 0 && (
              <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">
                ({runs.length})
              </span>
            )}
          </h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search runs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none"
            />
          </div>
          {search && (
            <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {sorted.length} of {runs.length}
            </span>
          )}
        </div>

        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[
                  ['initiated_at',    'Started'],
                  ['fiscal_period_id', 'Period'],
                  ['run_type',         'Run Type'],
                  ['run_status',       'Status'],
                  ['duration_ms',      'Duration'],
                  ['phase_count',      'Phases'],
                ].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => toggleSort(col)}>
                    {label}
                    <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
                    Loading calculation runs…
                  </td>
                </tr>
              )}
              {!isLoading && sorted.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">
                    {search ? 'No runs match your search.' : 'No calculation runs found.'}
                  </td>
                </tr>
              )}
              {!isLoading && sorted.map((row) => (
                <tr
                  key={row.calculation_run_id}
                  onClick={() => handleSelectRun(row)}
                  className={`border-t border-[rgb(var(--color-border))] cursor-pointer transition-colors hover:bg-[rgb(var(--color-surface-muted))] ${
                    selectedRun?.calculation_run_id === row.calculation_run_id
                      ? 'bg-[rgb(var(--color-surface-muted))]'
                      : ''
                  }`}
                >
                  <td className={tdCls}>{formatTimestamp(row.initiated_at)}</td>
                  <td className={tdCls}>{row.fiscal_period_id ?? '—'}</td>
                  <td className={tdCls}><RunTypeBadge runType={row.run_type} /></td>
                  <td className={tdCls}><StatusBadge status={row.run_status} /></td>
                  <td className={tdCls}>{formatDuration(row.duration_ms)}</td>
                  <td className={tdCls}>{row.phase_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <button
            type="button"
            onClick={() => loadRuns({ cursor: nextCursor, append: true, activeFilters: filters })}
            disabled={isLoadingMore}
            className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 self-start"
          >
            {isLoadingMore ? 'Loading more…' : 'Load More'}
          </button>
        )}
      </div>

      {/* Detail side panel — matches ImportLogViewer pattern exactly */}
      {selectedRun && (
        <>
          <div className="fixed inset-0 z-30" onClick={handleClosePanel} />
          <div className="fixed top-0 right-0 z-40 h-full bg-[rgb(var(--color-surface))] border-l border-[rgb(var(--color-border))] shadow-xl flex flex-col" style={{ width: panelWidth }}>
            {/* Drag handle — S946 */}
            <div {...dragHandleProps} />

            {/* Panel header */}
            <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 shrink-0">
              <div>
                <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
                  Calculation Run
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <RunTypeBadge runType={selectedRun.run_type} />
                  <StatusBadge status={selectedRun.run_status} />
                </div>
              </div>
              <button
                onClick={handleClosePanel}
                className="rounded-lg p-1 hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">

              {/* Detail grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Run Type',       <RunTypeBadge runType={selectedRun.run_type} />],
                  ['Status',         <StatusBadge status={selectedRun.run_status} />],
                  ['Period',         selectedRun.fiscal_period_id ?? '—'],
                  ['Positions Calculated', selectedRun.positions_calculated ?? '—'],
                  ['Positions in Scope',  selectedRun.positions_in_scope  ?? '—'],
                  ['Started',        formatTimestamp(selectedRun.initiated_at)],
                  ['Completed',      formatTimestamp(selectedRun.completed_at)],
                  ['Duration',       formatDuration(selectedRun.duration_ms)],
                  ['Phases',         selectedRun.phase_count ?? '—'],
                  ['Initiated By',   <span className="font-mono text-tok-xs" title={selectedRun.initiated_by}>{truncateId(selectedRun.initiated_by)}</span>],
                  ['Run ID',         <span className="font-mono text-tok-xs select-all">{selectedRun.calculation_run_id ?? '—'}</span>],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-col gap-0.5">
                    <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{label}</span>
                    <span className="text-tok-sm text-[rgb(var(--color-text))] break-all">{value}</span>
                  </div>
                ))}
              </div>

              {/* Error detail — only when failed */}
              {selectedRun.run_status === 'failed' && selectedRun.error_detail && (
                <div>
                  <p className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1">Error Detail</p>
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">{selectedRun.error_detail}</p>
                </div>
              )}

              {/* Phase trace sub-table */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                  <h4 className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">
                    Phase Trace
                  </h4>
                  {telemetry.length > 0 && (
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                      ({telemetry.length} events)
                    </span>
                  )}
                </div>

                {isLoadingTelem && (
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading phase trace…</p>
                )}

                {telemError && (
                  <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                    {telemError}
                  </div>
                )}

                {!isLoadingTelem && !telemError && telemetry.length === 0 && (
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                    No telemetry available for this run.
                  </p>
                )}

                {!isLoadingTelem && !telemError && telemetry.length > 0 && (
                  <div className="overflow-auto rounded-lg border border-[rgb(var(--color-border))]">
                    <table className="w-full text-tok-xs">
                      <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))]">
                        <tr>
                          {['Phase', 'Key', 'Value', 'Status', 'Duration'].map((h) => (
                            <th key={h} className="px-2 py-1.5 text-left font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {telemetry.map((t, i) => (
                          <tr
                            key={i}
                            className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]"
                          >
                            <td className="px-2 py-1.5 text-[rgb(var(--color-text))] whitespace-nowrap font-medium">
                              {t.display_phase || '—'}
                            </td>
                            <td className="px-2 py-1.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap font-mono">
                              {t.telemetry_key || '—'}
                            </td>
                            <td className="px-2 py-1.5 text-[rgb(var(--color-text))] max-w-[140px] truncate" title={t.telemetry_value || ''}>
                              {t.telemetry_value || '—'}
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap">
                              <PhaseBadge status={t.status} />
                            </td>
                            <td className="px-2 py-1.5 text-[rgb(var(--color-text-muted))] whitespace-nowrap">
                              {formatDuration(t.duration_ms)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
